import { useState } from "react";
import { styled } from "styled-components";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { MdOutlineArrowOutward } from "react-icons/md";

import HomeContainerWrapper from "../../components/HomeContainerWrapper";
import HomeHeading from "../../components/HomeHeading";
import curious from "../../assets/docs/curious.json";

const revealEase = [0.22, 1, 0.36, 1];

export default function Index() {
  const [activeIndex, setActiveIndex] = useState(0);

  const disableImage = (event) => event.preventDefault();

  const nextCard = () => {
    setActiveIndex((prev) => (prev + 1) % curious.length);
  };

  const previousCard = () => {
    setActiveIndex((prev) => (prev - 1 + curious.length) % curious.length);
  };

  return (
    <Container id="curious">
      <HomeContainerWrapper>
        <HomeHeading>Questions Explored</HomeHeading>

        <CardSection
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.72, ease: revealEase }}
        >
          <Controls
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.5, delay: 0.08, ease: revealEase }}
          >
            <NavButton onClick={previousCard} aria-label="Previous card">
              <IoIosArrowBack />
            </NavButton>

            <NavButton onClick={nextCard} aria-label="Next card">
              <IoIosArrowForward />
            </NavButton>
          </Controls>

          <Stack>
            {curious.map((item, idx) => {
              const position =
                (idx - activeIndex + curious.length) % curious.length;

              return (
                <Box key={idx} $position={position} $total={curious.length}>
                  <ImageContainer $active={position === 0}>
                    <ImageGlow />

                    <img
                      src={item.thumbnail}
                      alt={item.name}
                      onContextMenu={disableImage}
                      onDragStart={disableImage}
                    />
                  </ImageContainer>

                  <Content $active={position === 0}>
                    <ContentInner>
                      <h3>{item.name}</h3>

                      <p>{item.what}</p>

                      <Link to={`/curious/${item.slug || item.nav}`}>
                        Find Out More
                        <MdOutlineArrowOutward />
                      </Link>
                    </ContentInner>
                  </Content>
                </Box>
              );
            })}
          </Stack>
        </CardSection>
      </HomeContainerWrapper>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  margin-top: 100px;

  @media (max-width: 768px) {
    margin-top: 30px;
  }
`;

const CardSection = styled(motion.div)`
  width: 100%;
  margin-top: 35px;
`;

const Controls = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  gap: 14px;

  width: 100%;
  max-width: 1050px;

  margin: 0 auto 28px;

  @media (max-width: 768px) {
    gap: 10px;
    margin-bottom: 22px;
  }
`;

const Stack = styled.div`
  position: relative;

  width: 100%;
  max-width: 1050px;

  height: 420px;

  margin: 65px auto 0;

  @media (max-width: 800px) {
    height: 550px;
    margin-top: 45px;
  }
`;

const Box = styled.div`
  position: absolute;
  inset: 0;

  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 35px;

  width: 100%;
  height: 100%;

  padding: 28px;

  border-radius: 10px;

  overflow: hidden;
  user-select: none;

  /* Match Experiences section */
  background: rgb(14, 14, 14);
  border: 1px solid #3d3d3d;

  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;

  transform: ${({ $position }) =>
    `translateY(-${$position * 28}px)
     scale(${1 - $position * 0.02})`};

  transform-origin: top center;

  z-index: ${({ $position, $total }) => $total - $position};

  opacity: ${({ $position }) => ($position > 3 ? 0 : 1)};

  pointer-events: ${({ $position }) => ($position === 0 ? "auto" : "none")};

  transition:
    transform 0.55s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.4s ease;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;

    gap: 15px;

    padding: 20px;

    transform: ${({ $position }) =>
      `translateY(-${$position * 16}px)
       scale(${1 - $position * 0.012})`};
  }
`;

const ImageContainer = styled.div`
  position: relative;

  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: visible;

  padding: 15px;

  img {
    position: relative;
    z-index: 2;

    display: block;

    width: 100%;
    height: 100%;

    max-width: 100%;
    max-height: 340px;

    object-fit: contain;

    pointer-events: none;
    user-select: none;

    filter: drop-shadow(0 20px 25px rgba(0, 0, 0, 0.3));
    opacity: ${({ $active }) => ($active ? 1 : 0.7)};
    transform: translateY(${({ $active }) => ($active ? "0" : "8px")});
    transition:
      opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1),
      transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  }

  @media (max-width: 800px) {
    height: 250px;

    padding: 5px;

    img {
      max-height: 240px;
    }
  }
`;

const ImageGlow = styled.div`
  position: absolute;

  width: 60%;
  height: 60%;

  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  border-radius: 50%;

  /*
    Keep the glow subtle so the card
    still feels like the Experiences section.
  */
  background: rgba(171, 171, 171, 0.08);

  filter: blur(50px);

  pointer-events: none;

  z-index: 1;
`;

const Content = styled.div`
  position: relative;
  z-index: 2;

  display: flex;
  align-items: center;

  width: 100%;
  height: 100%;

  padding: 10px;

  opacity: ${({ $active }) => ($active ? 1 : 0.62)};
  transform: translateY(${({ $active }) => ($active ? "0" : "10px")});
  transition:
    opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
`;

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  h3 {
    margin: 0 0 18px;

    font-size: clamp(24px, 3vw, 36px);
    line-height: 1.3;

    font-weight: 400;

    letter-spacing: -0.03em;

    color: #fff;
  }

  p {
    margin: 0;

    max-width: 390px;

    font-size: 14px;
    line-height: 1.7;

    font-weight: 300;

    color: #abababff;
  }

  a {
    display: inline-flex;
    align-items: center;

    gap: 7px;

    margin-top: 28px;
    margin-left: auto;

    width: fit-content;

    font-size: 14px;
    line-height: 1.7;

    font-weight: 300;

    color: #abababff;

    text-decoration: underline;

    transition:
      color 0.2s ease-out,
      transform 0.2s ease-out;

    svg {
      font-size: 17px;

      transition: transform 0.2s ease-out;
    }

    &:hover {
      color: #fff;

      svg {
        transform: translate(2px, -2px);
      }
    }
  }

  @media (max-width: 800px) {
    h3 {
      margin-bottom: 12px;

      font-size: 24px;
    }

    p {
      font-size: 13px;
      line-height: 1.6;
    }

    a {
      margin-top: 16px;
      margin-left: auto;

      font-size: 13px;

      svg {
        font-size: 16px;
      }
    }
  }
`;

const NavButton = styled.button`
  width: 48px;
  height: 48px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: 1px solid #abababff;
  border-radius: 50%;

  background: transparent;

  color: #abababff;

  cursor: pointer;

  font-weight: 300;

  transition:
    background 0.2s ease-out,
    color 0.2s ease-out,
    transform 0.2s ease-out;

  svg {
    font-size: 22px;
  }

  &:hover {
    background: #fff;
    color: #000;
  }

  &:active {
    transform: scale(0.94);
  }

  @media (max-width: 768px) {
    width: 42px;
    height: 42px;

    svg {
      font-size: 20px;
    }
  }
`;
